import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { PublicFiche } from '../../types';
import { SolutionIcon, CaCityIcon } from '../_icons';
import { formatSolution, getJurisdictionStyle, getCaCity } from '../_utils';
import { PROC_LINK_CONFIG } from '../../citeTypeConfig';

// ── Badges publication ────────────────────────────────────────────────────────
interface PubBadge { letter: string; labelKey: string; icon: string; color: string; }
const PUB_CONFIG: Record<string, PubBadge> = {
  'b': { letter: 'B', labelKey: 'fiche.pub.bulletin',   icon: '📋', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'r': { letter: 'R', labelKey: 'fiche.pub.rapport',    icon: '📖', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  'l': { letter: 'L', labelKey: 'fiche.pub.lettre',     icon: '✉️', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  'c': { letter: 'C', labelKey: 'fiche.pub.communique', icon: '📢', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  'p': { letter: 'P', labelKey: 'fiche.pub.bulletin',   icon: '📋', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'i': { letter: 'I', labelKey: 'fiche.pub.internet',   icon: '🌐', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
};

/** `t` fourni par useTranslation() de l'appelant. */
export const getPubBadges = (pub: string[] | undefined, t: TFunction): React.ReactNode => {
  if (!pub || pub.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 ring-1 ring-zinc-300 dark:ring-zinc-600"
        title={t('fiche.pub.unpublished_title_short')}
      >
        <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
        {t('fiche.pub.unpublished')}
      </span>
    );
  }
  const normalized = pub.map(p => p.toLowerCase());
  const hasB = normalized.includes('b') || normalized.includes('p');
  const hasR = normalized.includes('r');
  if (hasB && hasR) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 ring-1 ring-emerald-300 dark:ring-emerald-700">
        {t('fiche.card.principle_br')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {normalized.filter(p => PUB_CONFIG[p]).map((p, i) => {
        const cfg = PUB_CONFIG[p];
        return (
          <span key={i} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
            {cfg.icon} {t(cfg.labelKey)}
          </span>
        );
      })}
    </span>
  );
};

// ── CardBadges ────────────────────────────────────────────────────────────────
interface CardBadgesProps {
  fiche:      PublicFiche;
  showScore?: boolean;
}

export const CardBadges = ({ fiche, showScore = false }: CardBadgesProps) => {
  const { t } = useTranslation();
  const jurStyle      = getJurisdictionStyle(fiche.jurisdiction);
  const isCA          = jurStyle.isCA;
  const caCity        = isCA ? getCaCity(fiche.location, fiche.location_label, fiche.slug) : '';
  const hasImpactes   = (fiche.arrets_impactes?.length ?? 0) > 0;
  const hasProcLink   = (fiche.procedure_graph?.length ?? 0) > 0;
  const solutionLabel = formatSolution(fiche.solution, t);
  const otherLabel    = t('fiche.solution.other');
  const showSolution  = fiche.solution && (fiche.solution !== 'other' || fiche.solution_raison);

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-5 pt-2.5 pb-0">
      {showScore && fiche.score != null && (
        <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 rounded-full">
          {t('fiche.card.relevant', { pct: Math.round(fiche.score! * 100) })}
        </span>
      )}

      {getPubBadges(fiche.publication, t)}

      {showSolution && (
        <span className="inline-flex items-center gap-1.5">
          <SolutionIcon solution={fiche.solution} size={22} />
          <span className="text-xs font-medium text-foreground">
            {solutionLabel && solutionLabel !== otherLabel ? solutionLabel : null}
            {fiche.solution_raison && (
              <span className="text-muted-foreground font-normal">
                {solutionLabel && solutionLabel !== otherLabel ? ' · ' : ''}
                {fiche.solution_raison.length > 40
                  ? fiche.solution_raison.slice(0, 40) + '…'
                  : fiche.solution_raison}
              </span>
            )}
          </span>
        </span>
      )}

      {hasImpactes && (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          {t('fiche.card.revirement')}
        </span>
      )}

      {hasProcLink && (fiche.procedure_graph ?? [])
        .reduce((acc, link) => {
          if (!acc.find(l => l.link_type === link.link_type)) acc.push(link);
          return acc;
        }, [] as NonNullable<typeof fiche.procedure_graph>)
        .slice(0, 2)
        .map((link, i) => {
          const known = PROC_LINK_CONFIG[link.link_type];
          const cfg = known
            ? { label: t(known.labelKey), color: known.color, icon: known.icon }
            : { label: link.link_type, color: 'bg-muted text-muted-foreground', icon: '↳' };
          return (
            <span key={i} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
              {cfg.icon} {cfg.label}
            </span>
          );
        })
      }

      {isCA && caCity && (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-sl-teal-l text-sl-teal border border-sl-teal-m">
          <CaCityIcon city={caCity} size={14} />
          {caCity}
        </span>
      )}
    </div>
  );
};